import { ICON_BASE_PATH } from "@/app/utils/config";
import { useEffect, useState } from "react";
import "../smeonboarding.css";
import AddressCommon from "@/app/components/common/actions/inputs-b/AddressCommon";
import EmailInput from "@/app/components/common/actions/inputs-b/EmailInput";
import Mobilefiled from "@/app/components/common/actions/inputs-b/Mobilefiled";
import TradeLicenseNumber from "@/app/components/common/actions/inputs-b/TradeLicenseNumber";
import DatePickerInput from "@/app/components/common/actions/inputs-b/DatePickerInput";
import Landline from "@/app/components/common/actions/inputs-b/Landline";
import GxPrimaryButton from "@/app/components/common/actions/buttons/GxPrimaryButton";
import ContactName from "@/app/components/common/actions/inputs-b/ContactName";
import CompanyName from "@/app/components/common/actions/inputs-b/CompanyName";
import CompanyPoBox from "@/app/components/common/actions/inputs-b/CompanyPoBox";
import Uploadfile from "@/app/components/common/actions/inputs-b/Uploadfile";
import RequiredMessage from "@/app/components/webloading/errorHandling/RequiredMessage";
import SubscriptionPlan from "./subscriptionPlan";
import GlobalCountry from "@/app/components/common/actions/inputs-b/GlobalCountry";
import SupportCountry from "@/app/components/common/actions/inputs-b/supportCountry";
import { gxglobalcountryApi } from "@/app/apiConfig/services/authAbstractApiLayer";
import { GlobalFileUpload } from "@/app/components/common/actions/inputs-b/globalFileUpload";

/* ─────────────────── types ─────────────────── */

interface FormFields {
  companyName: string;
  contactName: string;
  country: string;
  address: string;
  email: string;
  mobile: string;
  mobileDialCode: string;
  tradeLicenseNumber: string;
  tradeLicenseExpiry: string;
  landline: string;
  poBox: string;
  tradeLicenseFile: File | null;
  ownershipDoc: File | null;
  orgLogo: File | null;
}

type FormErrors = Partial<Record<keyof FormFields, string>>;

interface CountryListItem {
  name: string;
  code2: string;
  checkBox: "Y" | "N";
}
interface CountryCodeEntry {
  code2: string;
  code3: string;
}

/* ─────────────────── validators ─────────────────── */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(fields: FormFields): FormErrors {
  const errors: FormErrors = {};

  if (!fields.companyName.trim())
    errors.companyName = "Please enter the company name.";

  if (!fields.contactName.trim())
    errors.contactName = "Please enter the contact name.";

  if (!fields.country.trim()) errors.country = "Please select the country.";

  if (!fields.address.trim())
    errors.address = "Please enter the organization address.";

  if (!fields.email.trim()) errors.email = "Please enter the email ID.";
  else if (!EMAIL_RE.test(fields.email.trim()))
    errors.email = "Please enter a valid email address.";

  if (!fields.mobile.replace(/\D/g, "").trim())
    errors.mobile = "Please enter the mobile number.";

  if (!fields.tradeLicenseNumber.trim())
    errors.tradeLicenseNumber = "Please enter the trade license number.";

  if (!fields.tradeLicenseExpiry)
    errors.tradeLicenseExpiry = "Please select the trade license expiry date.";

  if (!fields.landline.trim())
    errors.landline = "Please enter the landline number.";

  if (!fields.poBox.trim()) errors.poBox = "Please enter the company PO Box.";

  if (!fields.tradeLicenseFile)
    errors.tradeLicenseFile = "Please upload the trade license.";

  if (!fields.ownershipDoc)
    errors.ownershipDoc = "Please upload the ownership document.";

  if (!fields.orgLogo) errors.orgLogo = "Please upload the organization logo.";

  return errors;
}

const RegisterFrom = () => {
  const [countries, setCountries] = useState<any[]>([]);
  const [fields, setFields] = useState<FormFields>({
    companyName: "",
    contactName: "",
    country: "",
    address: "",
    email: "",
    mobile: "",
    mobileDialCode: "",
    tradeLicenseNumber: "",
    tradeLicenseExpiry: "",
    landline: "",
    poBox: "",
    tradeLicenseFile: null,
    ownershipDoc: null,
    orgLogo: null,
  });

  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [countryList, setCountryList] = useState<CountryListItem[]>([]);
  const [countryLoading, setCountryLoading] = useState(true);
  const [errors, setErrors] = useState<FormErrors>({});
  const [triggerValidation, setTriggerValidation] = useState(false);
  const [subscription, setsubscription] = useState(false);
  const [countryCodeMap, setCountryCodeMap] = useState<
    Record<string, CountryCodeEntry>
  >({});
  const [alert, setAlert] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const toggleCountry = (name: string) => {
    setCountryList((prev) =>
      prev.map((c) =>
        c.name === name
          ? { ...c, checkBox: c.checkBox === "Y" ? "N" : "Y" }
          : c,
      ),
    );
  };
  const supportCountries = countryList.filter(
    (country) => country?.checkBox === "Y",
  );

  const {
    handleLogoUpload,
    handleDocUpload,
    handlePdfUpload,
    fileError: uploadHookError,
  } = GlobalFileUpload();

  const set = (key: keyof FormFields) => (value: string | number) => {
    const updatedValue = typeof value === "number" ? value.toString() : value;
    setFields((prev) => ({ ...prev, [key]: updatedValue }));
    setErrors((prev) => {
      const n = { ...prev };
      delete n[key];
      return n;
    });
  };

  const setFile = (key: keyof FormFields) => async (file: File | null) => {
    if (!file) {
      setFields((prev) => ({ ...prev, [key]: null }));
      return;
    }

    if (key === "tradeLicenseFile") {
      const isPdf =
        file.type === "application/pdf" ||
        file.name.toLowerCase().endsWith(".pdf");

      if (!isPdf) {
        setErrors((prev) => ({
          ...prev,
          tradeLicenseFile: "Only PDF files are allowed.",
        }));
        return;
      }
    }

    setFields((prev) => ({ ...prev, [key]: file }));

    setErrors((prev) => {
      const n = { ...prev };
      delete n[key];
      return n;
    });

    try {
      if (key === "tradeLicenseFile") {
        await handleDocUpload(file);
      } else if (key === "ownershipDoc") {
        await handlePdfUpload(file);
      } else if (key === "orgLogo") {
        await handleLogoUpload(file);
      }
    } catch {
      setErrors((prev) => ({
        ...prev,
        [key]: "Upload failed. Please try again.",
      }));
    }
  };

  const handleContinue = () => {
    setTriggerValidation(true);
    const validationErrors = validate(fields);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setAlert({
        type: "error",
        message: "Please fill in all required fields before continuing.",
      });

      setTimeout(() => {
        const el = document.querySelector(
          ".gx_input_validationmessage, .gx-upload-dropzone-error",
        );
        if (el) {
          el.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 100);

      return;
    }
    if (alreadyRegistered === true) {
      setAlert({
        type: "error",
        message: "This company name is already registered.",
      });

      setTimeout(() => {
        const el = document.querySelector(".gx_input_validationmessage");

        if (el) {
          el.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 100);

      return;
    }
    setErrors({});
    setAlert(null);
    setTriggerValidation(false);
    setsubscription(true);
  };

  useEffect(() => {
    if (triggerValidation) {
      const t = setTimeout(() => setTriggerValidation(false), 300);
      return () => clearTimeout(t);
    }
  }, [triggerValidation]);

  useEffect(() => {
    const fetchCountries = async () => {
      setCountryLoading(true);
      try {
        const response = await gxglobalcountryApi({ type: "countryName" });
        const data = response?.data ?? response?.result ?? [];
        const countryObjects: any[] = Array.isArray(data)
          ? data
              .map((item: any) => ({
                name: item.countryName,
                code2: item.countryCode2,
              }))
              .filter((item) => item.name)
          : [];

        setCountries(countryObjects);
        const codeMap: Record<string, CountryCodeEntry> = {};
        if (Array.isArray(data)) {
          data.forEach((item: any) => {
            if (item?.countryCode3) {
              codeMap[item.countryCode3] = {
                code2: item.countryCode2 ?? "",
                code3: item.countryCode3,
              };
            }
          });
        }
        setCountryCodeMap(codeMap);
        const storedCodes = (sessionStorage.getItem("supportedCountries") || "")
          .split(",")
          .map((c) => c.trim().toUpperCase())
          .filter(Boolean);

        const filteredItems: CountryListItem[] = Array.isArray(data)
          ? data
              .filter((item: any) =>
                storedCodes.includes((item?.countryCode2 || "").toUpperCase()),
              )
              .map((item: any) => ({
                name: item.countryName as string,
                code2: item.countryCode2 as string,
                checkBox: "Y" as const,
              }))
          : [];
        setCountryList(filteredItems);
      } catch (error) {
        console.error("Error fetching countries:", error);
      } finally {
        setCountryLoading(false);
      }
    };
    fetchCountries();
  }, []);

  return (
    <>
      {subscription == true ? (
        <>
          <SubscriptionPlan
            setsubscription={setsubscription}
            formData={{
              ...fields,
              country: countryCodeMap[fields.country]?.code2 ?? fields.country,
            }}
            supportCountries={supportCountries}
          />
        </>
      ) : (
        <>
          <div className="gx-smeonboard-card">
            <div className="gx-title-bank">
              <h2 className="gx-h2-l">
                Register
                <span className="gx-mpin-heading-bold gx-h2-b">
                  {" "}
                  Your Business
                </span>
              </h2>
            </div>
            <p className="gx-p12-m">
              Please provide your registered office details and official trade
              license to complete your profile.
            </p>
            <div className="gx-smeonboard-overflow">
              <div className="gx-smeonboard-child mt-5">
                <div className="grid grid-cols-12 gap-4 w-full">
                  <div className="col-span-6 mb-3">
                    <CompanyName
                      label="Company Name"
                      value={fields.companyName}
                      onChange={set("companyName")}
                      validationMessage={errors.companyName}
                      setAlreadyRegistered={setAlreadyRegistered}
                    />
                  </div>
                  <div className="col-span-6 mb-3">
                    <ContactName
                      label="Contact Name"
                      value={fields.contactName}
                      onChange={set("contactName")}
                      validationMessage={errors.contactName}
                    />
                  </div>
                  <div className="col-span-6 mb-3">
                    <GlobalCountry
                      countries={countries}
                      label="Select Country"
                      value={fields.country}
                      onChange={set("country")}
                      validationMessage={errors.country}
                      triggerValidation={triggerValidation}
                    />
                  </div>
                  <div className="col-span-6 mb-3">
                    <AddressCommon
                      label="Organization Address"
                      value={fields.address}
                      onChange={set("address")}
                      validationMessage={errors.address}
                    />
                  </div>
                  <div className="col-span-6 mb-3">
                    <EmailInput
                      label="Email ID"
                      value={fields.email}
                      onChange={set("email")}
                      triggerValidation={triggerValidation}
                      fieldName="email"
                    />
                  </div>
                  <div className="col-span-6 mb-3">
                    <Mobilefiled
                      label="Mobile Number"
                      value={fields.mobile}
                      onChange={set("mobile")}
                      onCountryCodeChange={set("mobileDialCode")}
                      countryDialCode={fields.mobileDialCode}
                      triggerValidation={triggerValidation}
                      fieldName="mobile"
                      mobileError={errors.mobile}
                    />
                  </div>
                  <div className="col-span-6 mb-3">
                    <TradeLicenseNumber
                      label="Trade License Number"
                      value={fields.tradeLicenseNumber}
                      onChange={set("tradeLicenseNumber")}
                      validationMessage={errors.tradeLicenseNumber}
                    />
                  </div>
                  <div className="col-span-6 mb-3">
                    <DatePickerInput
                      label="Trade License Expiry Date"
                      iconSrc={`${ICON_BASE_PATH}date.svg`}
                      value={fields.tradeLicenseExpiry}
                      onChange={set("tradeLicenseExpiry")}
                      validationMessage={errors.tradeLicenseExpiry}
                    />
                  </div>
                  <div className="col-span-6 mb-3">
                    <Landline
                      label="Landline Number"
                      value={fields.landline}
                      onChange={set("landline")}
                      validationMessage={errors.landline}
                    />
                  </div>
                  <div className="col-span-6 mb-3">
                    <CompanyPoBox
                      label="Company PO Box"
                      value={fields.poBox}
                      onChange={set("poBox")}
                      validationMessage={errors.poBox}
                    />
                  </div>
                  <div className="col-span-12 mb-3">
                    <SupportCountry
                      countryList={countryList}
                      onToggle={toggleCountry}
                      loading={countryLoading}
                      label="Supported Countries"
                      disabled
                    />
                  </div>
                  <div className="col-span-12 mb-3">
                    <div className="flex items-center mb-4">
                      <div className="grow border-t border-[#EEEFF2]" />
                    </div>
                  </div>
                  <div className="col-span-12">
                    <p className="gx-p14-b text-[#2E3338]">Upload Documents</p>
                  </div>
                  <div className="col-span-6 mb-3">
                    <Uploadfile
                      value={fields.tradeLicenseFile}
                      onChange={setFile("tradeLicenseFile")}
                      validationMessage={errors.tradeLicenseFile}
                      label="1.Upload Trade License"
                      touched={true}
                      accept=".pdf,application/pdf"
                    />
                  </div>
                  <div className="col-span-6 mb-3">
                    <Uploadfile
                      validationMessage={errors.ownershipDoc}
                      value={fields.ownershipDoc}
                      onChange={setFile("ownershipDoc")}
                      label="2.Ownership Document"
                      touched={true}
                    />
                  </div>
                  <div className="col-span-6">
                    <Uploadfile
                      value={fields.orgLogo}
                      onChange={setFile("orgLogo")}
                      validationMessage={errors.orgLogo}
                      label="3.Organization Logo"
                      touched={true}
                    />
                  </div>
                  {uploadHookError && (
                    <div className="col-span-12">
                      <RequiredMessage message={uploadHookError} />
                    </div>
                  )}
                  {alert && (
                    <div className="col-span-12">
                      <RequiredMessage message={alert.message} />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="gx-footer-btn-end">
              <GxPrimaryButton handleSubmit={handleContinue} label="CONTINUE" />
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default RegisterFrom;
